import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppBar } from "../../components/AppShell";
import { useToast } from "../../components/Toast";
import { request } from "../../lib/api";
import { initials } from "../../lib/helpers";
import type { User } from "../../lib/types";
import { UserModal } from "./Students";

export function AdminAdministrators() {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<User | null | "new">(null);

  const { data: all = [] } = useQuery({
    queryKey: ["admin", "administrators"],
    queryFn: () => request<User[]>("/users/admins"),
  });

  const create = useMutation({
    mutationFn: (body: object) => request("/admin/users", { method: "POST", body }),
    onSuccess: () => { toast("Administrator adăugat", "success"); qc.invalidateQueries({ queryKey: ["admin", "administrators"] }); setEditing(null); },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: object }) => request(`/admin/users/${id}`, { method: "PATCH", body }),
    onSuccess: () => { toast("Administrator actualizat", "success"); qc.invalidateQueries({ queryKey: ["admin", "administrators"] }); setEditing(null); },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });
  const del = useMutation({
    mutationFn: (id: string) => request(`/admin/users/${id}`, { method: "DELETE" }),
    onSuccess: () => { toast("Administrator șters", "success"); qc.invalidateQueries({ queryKey: ["admin", "administrators"] }); setEditing(null); },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  const ROLE_LABELS: Record<string, { cls: string; icon: string; label: string }> = {
    super_admin: { cls: "role-superadmin", icon: "fa-crown", label: "Super admin" },
    editor: { cls: "role-editor", icon: "fa-pen-to-square", label: "Editor curs" },
    examinator: { cls: "role-examinator", icon: "fa-clipboard-check", label: "Examinator" },
    support: { cls: "role-support", icon: "fa-headset", label: "Suport" },
  };

  return (
    <>
      <AppBar crumbs={[{ label: "Tablou de bord", to: "/admin/dashboard" }, { label: "Administratori" }]} />
      <div className="content">
        <div className="page-head">
          <div><h1>Administratori</h1><p>Gestionează echipa care administrează platforma.</p></div>
          <div className="page-actions">
            <button className="btn btn-primary" onClick={() => setEditing("new")}>
              <i className="fa-solid fa-user-plus"></i> Adaugă administrator
            </button>
          </div>
        </div>

        <div className="card">
          <div className="table-wrap">
            <table className="data">
              <thead><tr><th>Administrator</th><th>Rol</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {all.length === 0 ? (
                  <tr><td colSpan={4} className="text-muted">Niciun administrator.</td></tr>
                ) : all.map((u) => {
                  const meta = ROLE_LABELS[u.role] || ROLE_LABELS.editor;
                  return (
                    <tr key={u.id} style={{ cursor: "pointer" }} onClick={() => setEditing(u)}>
                      <td>
                        <div className="user-cell">
                          <div className="avatar avatar-sm" style={{ background: "linear-gradient(135deg, var(--c-accent), #8a0a26)" }}>{initials(u.full_name)}</div>
                          <div><strong>{u.full_name}</strong><br /><small className="muted">{u.email}</small></div>
                        </div>
                      </td>
                      <td><span className={"role-pill " + meta.cls}><i className={`fa-solid ${meta.icon}`}></i> {meta.label}</span></td>
                      <td>{u.is_active ? <span className="pill success">Activ</span> : <span className="pill danger">Suspendat</span>}</td>
                      <td className="text-right">
                        <button className="icon-btn" style={{ width: 32, height: 32 }} onClick={(e) => { e.stopPropagation(); setEditing(u); }}>
                          <i className="fa-solid fa-pen"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <UserModal
          open={editing !== null}
          user={editing && editing !== "new" ? editing : null}
          mode="admin"
          onClose={() => setEditing(null)}
          onSubmit={(payload) => {
            if (editing === "new") create.mutate(payload);
            else if (editing) update.mutate({ id: editing.id, body: payload });
          }}
          onDelete={editing && editing !== "new" ? () => {
            if (confirm("Sigur ștergi acest administrator?")) del.mutate(editing.id);
          } : undefined}
        />
      </div>
    </>
  );
}
